import { mergeClasses } from 'minimal-shared/utils';

import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import ButtonBase from '@mui/material/ButtonBase';

import { Iconify } from 'src/components/iconify';
import {
  navItemStyles,
  navSectionClasses,
  createNavItem as createNavItemBase,
} from 'src/components/nav-section';

// ----------------------------------------------------------------------

// createNavItem 은 아직 JS 라 인자 전부가 필수로 추론된다 — 실제 계약(전부 선택)으로 좁혀 쓴다 (로직 무변)
const createNavItem = createNavItemBase as unknown as (args: {
  path?: string;
  hasChild?: boolean;
  externalLink?: boolean;
}) => { baseProps: Record<string, unknown>; renderIcon?: React.ReactNode; renderInfo?: React.ReactNode };

type NavItemStateProps = {
  open?: boolean;
  active?: boolean;
  variant?: string;
};

type NavItemProps = React.ComponentProps<typeof ButtonBase> &
  NavItemStateProps & {
    title?: React.ReactNode;
    icon?: React.ReactNode;
    path?: string;
    subItem?: boolean;
    hasChild?: boolean;
    externalLink?: boolean;
  };

export function NavItem({
  title,
  icon,
  path,
  /********/
  open,
  active,
  /********/
  subItem,
  hasChild,
  className,
  externalLink,
  ...other
}: NavItemProps) {
  const navItem = createNavItem({ path, hasChild, externalLink });

  const ownerState = { open, active, variant: !subItem ? 'rootItem' : 'subItem' };

  return (
    <ItemRoot
      disableRipple
      aria-label={title}
      {...ownerState}
      {...navItem.baseProps}
      className={mergeClasses([navSectionClasses.item.root, className], {
        [navSectionClasses.state.open]: open,
        [navSectionClasses.state.active]: active,
      })}
      {...other}
    >
      <ItemTitle {...ownerState}>
        <Stack direction="row" alignItems="center" gap={1}>
          {icon}
          {title}
        </Stack>
      </ItemTitle>

      {hasChild && <ItemArrow {...ownerState} icon="solar:alt-arrow-down-linear" />}
    </ItemRoot>
  );
}

// ----------------------------------------------------------------------

const shouldForwardProp = (prop: string) => !['open', 'active', 'variant', 'sx'].includes(prop);

/**
 * @slot root
 */
const ItemRoot = styled(ButtonBase, { shouldForwardProp })<NavItemStateProps>(({ active, theme }) => ({
  color: theme.palette.common.black,
  transition: theme.transitions.create(['color'], {
    duration: theme.transitions.duration.shorter,
  }),
  // 기본 active 스타일
  ...(active && {
    color: theme.palette.primary.main,
    zIndex: 1,
  }),
  // 호버 시 본인만 검정, 동시에 다른 active는 회색
  '&:hover': {
    color: theme.palette.common.black,
    zIndex: 2,
  },
  // 부모(ul 등)에서 hover 중인데 본인은 active지만 hover가 아닐 때 회색 처리
  // (다른 아이템이 hover된 경우)
  '.MuiList-root:hover &, ul:hover &': {
    ...(active && {
      // 단, 본인이 hover 중이면 블랙 유지
      '@media (hover: hover)': {
        '&:hover, &:focus-visible': {
          color: theme.palette.common.black,
          zIndex: 2,
        },
      },
      // 본인이 hover가 아닐 때만 회색 처리
      color: theme.palette.text.secondary,
    }),
  },
  // 액티브 + 호버일 때 블랙 처리 (우선순위 보장)
  ...(active && {
    '&:hover, &:focus-visible': {
      color: theme.palette.common.black,
      zIndex: 2,
    },
  }),
  // 비액티브가 호버/포커스일 때
  ...(!active && {
    '&:hover, &:focus-visible': {
      color: theme.palette.common.black,
    },
  }),
}));

/**
 * @slot title
 */
const ItemTitle = styled('span', { shouldForwardProp })<NavItemStateProps>(({ theme }) => ({
  ...navItemStyles.title(theme),
  ...theme.typography.body2,
  fontWeight: theme.typography.fontWeightMedium,
  variants: [
    // fontWeightSemiBold 는 테마(core/typography.js)엔 실재하나 mui 증강 타입에 빠져 있다 — 국소 좁힘 (로직 무변)
    { props: { variant: 'subItem' }, style: { fontSize: theme.typography.pxToRem(14), fontWeight: (theme.typography as { fontWeightSemiBold?: string }).fontWeightSemiBold } },
    { props: { active: true }, style: { fontWeight: (theme.typography as { fontWeightSemiBold?: string }).fontWeightSemiBold } },
  ],
}));


/**
 * @slot arrow
 */
const ItemArrow = styled(Iconify, { shouldForwardProp })(({ theme }) => ({
  ...navItemStyles.arrow(theme),
}));
